<template>
  <div>
    <b-row class="d-flex align-items-center">
      <b-col cols="8">
        <h1 class="font-weight-bolder mb-0">Odds Boost Configuration</h1>
      </b-col>
      <b-col cols="4" class="text-right">
        <b-button
          @click="$router.push('/promotions/odd-boost')"
          variant="primary"
        >
          <feather-icon icon="ArrowLeftIcon" />
          Back to List
        </b-button>
      </b-col>
    </b-row>

    <b-row class="mt-2">
      <validation-observer ref="validateOddBoostForm">
        <b-col cols="12">
          <b-card>
            <b-row>
              <b-col cols="12">
                <h5 class="font-weight-bolder text-body">
                  Boost Details
                </h5></b-col
              >
              <b-col cols="12" md="7">
                <p class="font-weight-bold mb-0">Odds Boost Type</p>
                <v-select
                  v-model="selectedBoostType"
                  :options="optionsBoostType"
                  :reduce="(val) => val.value"
                  :clearable="false"
                  class="w-100"
                />
              </b-col>
              <b-col cols="12" md="5">
                <p class="font-weight-bold mb-0">
                  Boost Display Name* (Internal Description)
                </p>
                <validation-provider
                  #default="{ errors }"
                  name="name"
                  rules="required"
                >
                  <b-form-input
                    placeholder="Weekend Football Boost"
                    v-model="name"
                    type="text"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-col>

              <b-col cols="12" class="mt-1">
                <p class="font-weight-bold mb-0">
                  Description (Customer Description)
                </p>
                <b-form-textarea
                  placeholder="Enter description"
                  rows="1"
                  no-resize
                  v-model="description"
                ></b-form-textarea>
              </b-col>
              <b-col cols="12" class="mt-1">
                <p class="font-weight-bold mb-0">Display UI Information</p>
                <vue2-tinymce-editor
                  :key="'additionalInfo'"
                  id="additionalInfo"
                  v-model="additionalInfo"
                  :options="{ ...editorOptions }"
                />
              </b-col>
            </b-row>

            <b-row class="mt-1">
              <b-col md="4">
                <p class="font-weight-bold mb-0">Boost Amount*</p>
                <b-input-group>
                  <b-form-input
                    v-model="boostAmount"
                    type="number"
                    placeholder="Enter amount"
                  ></b-form-input>

                  <b-input-group-append>
                    <div
                      class="border d-flex justify-content-between align-items-center"
                      style="padding: 2px 6px; background: #e9ecef"
                    >
                      <feather-icon
                        :icon="boostUnit === '%' ? 'PercentIcon' : 'LayersIcon'"
                        style="color: black"
                      />
                    </div>
                    <b-form-select
                      v-model="boostUnit"
                      :options="unitOptions"
                      class="border-left-0"
                      style="border-radius: 0px 4px 4px 0px"
                    ></b-form-select>
                  </b-input-group-append>
                </b-input-group>
              </b-col>

              <b-col md="5">
                <p class="font-weight-bold mb-0">Max Stake</p>
                <b-input-group size="md" prepend="$">
                  <b-form-input
                    v-model="maxStake"
                    placeholder="50"
                    :disabled="selectedMaxStakeType !== 'Amount'"
                  >
                  </b-form-input>
                  <b-input-group-append>
                    <b-dropdown
                      :text="selectedMaxStakeType"
                      style="width: 152px"
                      variant="outline-secondary"
                    >
                      <b-dropdown-item @click="handleSelectMaxStake('Amount')"
                        >Amount</b-dropdown-item
                      >
                      <b-dropdown-item
                        @click="handleSelectMaxStake('Profile Limits')"
                        >Profile Limits</b-dropdown-item
                      >
                    </b-dropdown>
                  </b-input-group-append>
                </b-input-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col cols="8" class="mt-1">
                <b-row>
                  <b-col md="6">
                    <p class="text-body font-weight-bolder mb-0">
                      Start Date / Time
                    </p>
                    <flat-pickr
                      id="date"
                      v-model="startDate"
                      class="form-control"
                      :config="{
                        enableTime: true,
                        dateFormat: 'Y-m-d H:i:s',
                        altInput: true,
                        altFormat: 'd/m/Y H:i (AEDT)',
                        static: true,
                      }"
                      placeholder="dd/mm/yyyy hh:mm"
                    />
                  </b-col>
                  <b-col md="6">
                    <p class="text-body font-weight-bolder mb-0">
                      End Date / Time
                    </p>
                    <flat-pickr
                      id="date"
                      v-model="endDate"
                      class="form-control"
                      :config="{
                        enableTime: true,
                        dateFormat: 'Y-m-d H:i:s',
                        altInput: true,
                        altFormat: 'd/m/Y H:i (AEDT)',
                        static: true,
                      }"
                      placeholder="dd/mm/yyyy hh:mm"
                    />
                  </b-col>
                </b-row>
              </b-col>
            </b-row>

            <b-row class="mt-2">
              <b-col cols="12"
                ><h5 class="font-weight-bolder text-body">
                  Eligible Markets & Usage Limits
                </h5></b-col
              >
              <!-- Racing Markets -->
              <b-col cols="12">
                <b-card bg-variant="light">
                  <b-row>
                    <b-col md="6">
                      <p class="font-weight-bold mb-0">Racing Markets</p>
                      <DualListbox
                        title="Racing Code"
                        :options="racingCodes"
                        @onSelectApplies="handleSelectAppliesRacingCodes"
                        :listSelected="selectedRules.racingCodes"
                      />
                      <DualListbox
                        title="Bet Types"
                        :options="betTypes"
                        @onSelectApplies="handleSelectAppliesBetTypes"
                        :listSelected="selectedRules.betTypes"
                      />
                      <DualListbox
                        title="Region"
                        :options="regions"
                        @onSelectApplies="handleSelectAppliesRegions"
                        :listSelected="selectedRules.regions"
                      />
                      <DualListbox
                        title="Track"
                        :options="trackList"
                        @onSelectApplies="handleSelectAppliesTrackList"
                        :listSelected="selectedRules.trackList"
                      />
                    </b-col>

                    <!-- Sports -->
                    <b-col md="6">
                      <p class="font-weight-bold mb-0">Sports</p>
                      <DualListbox
                        title="Select Sports"
                        :options="sports"
                        @onSelectApplies="handleSelectAppliesSports"
                        :listSelected="selectedRules.sports"
                      />
                      <!-- <DualListbox
                      title="Specific Leagues"
                      :options="leagues"
                      @onSelectApplies="handleSelectApplies"
                    /> -->
                      <DualListbox
                        title="Eligible Markets"
                        :options="eligibleMarkets"
                        :listSelected="selectedRules.eligibleMarkets"
                        @onSelectApplies="handleSelectAppliesEligibleMarkets"
                      />
                      <!-- <DualListbox
                      title="Specific Events"
                      :options="eligibleMarkets"
                    /> -->
                    </b-col>
                  </b-row>
                </b-card>
              </b-col>
            </b-row>

            <b-row class="mt-2">
              <b-col
                cols="12"
                class="d-flex align-items-center justify-content-between mb-1"
                ><h5 class="font-weight-bolder text-body">
                  Recurring Boost Token Issuance
                </h5>
                <input
                  ref="fileInput"
                  type="file"
                  accept=".csv"
                  hidden
                  @change="handleFileUpload"
                />
                <b-button
                  size="sm"
                  variant="outline-primary"
                  @click="onClickInput"
                >
                  <feather-icon icon="UploadIcon" />

                  CSV File Upload
                </b-button>
              </b-col>

              <b-col
                cols="12"
              >
                <b-card bg-variant="light">
                  <b-tabs active-nav-item-class="bg-light">
                    <b-tab title="Customer Profiles" active>
                      <draggable
                        :list="tagCustomerProfiles"
                        :group="{ name: 'tag', pull: 'clone', put: false }"
                        @change="handleDrag"
                        :sort="false"
                        class="d-flex flex-wrap w-100"
                      >
                        <b-col
                          v-for="(tag, index) in tagCustomerProfiles"
                          :key="index"
                          cols="6"
                          md="2"
                          style="padding: 6px"
                        >
                          <b-card
                            border-variant="secondary"
                            body-class="p-0"
                            class="mb-0"
                            style="padding: 8px; border-radius: 4px"
                            :data-item="JSON.stringify(tag)"
                            ><span>{{ tag.profileSegment }}</span></b-card
                          >
                        </b-col>
                      </draggable>
                    </b-tab>
                    <b-tab title="Customer Segments" disabled style="cursor: default; opacity: 0.6; user-select: none">
                      <draggable
                        :list="tagCustomerSegments"
                        :group="{ name: 'tag', pull: 'clone', put: false }"
                        @change="handleDrag"
                        class="d-flex flex-wrap w-100"
                        :sort="false"
                      >
                        <b-col
                          v-for="(tag, index) in tagCustomerSegments"
                          :key="index"
                          cols="6"
                          md="2"
                          style="padding: 6px"
                        >
                          <b-card
                            border-variant="secondary"
                            body-class="p-0"
                            class="mb-0"
                            style="padding: 8px; border-radius: 4px"
                            :data-item="JSON.stringify(tag)"
                            ><span>{{ tag.profileSegment }}</span></b-card
                          >
                        </b-col>
                      </draggable>
                    </b-tab>
                  </b-tabs>
                </b-card>
              </b-col>
            </b-row>

            <!-- User Picker Section -->
            <b-row class="mt-2">
              <b-col cols="12" class="mb-1">
                <h5 class="font-weight-bolder text-body">Customers</h5>
              </b-col>
              <b-col cols="12" md="6">
                <b-card no-body class="border" style="overflow: hidden">
                  <!-- Sticky Search -->
                  <div
                    class="p-1"
                    style="
                      border-bottom: 1px solid #e0e0e0;
                      position: sticky;
                      top: 0;
                      z-index: 2;
                      background: white;
                    "
                  >
                    <b-form-input
                      v-model="userPickerSearch"
                      placeholder="Search by username..."
                    />
                  </div>

                  <!-- Scrollable User List -->
                  <div
                    ref="userPickerList"
                    style="max-height: 320px; overflow-y: auto"
                    @scroll="handleUserListScroll"
                  >
                    <div
                      v-if="userPickerLoading && userPickerUsers.length === 0"
                      class="text-center py-3 text-muted"
                    >
                      <b-spinner small /> Loading...
                    </div>
                    <div
                      v-else-if="
                        !userPickerLoading && userPickerUsers.length === 0
                      "
                      class="text-center py-3 text-muted"
                    >
                      No users found
                    </div>
                    <div
                      v-for="user in userPickerUsers"
                      :key="user.userId"
                      class="d-flex align-items-center px-2 py-1"
                      style="
                        border-bottom: 1px solid #f0f0f0;
                        cursor: pointer;
                        transition: background 0.15s;
                      "
                      :style="
                        isUserConfirmed(user.userId)
                          ? 'background:#f0fff4'
                          : userPickerVerifyingId === user.userId
                          ? 'background:#f8f9fa'
                          : ''
                      "
                      @click="handleSelectUser(user)"
                    >
                      <b-avatar
                        size="28"
                        :text="
                          (user.firstName ||
                            user.username ||
                            '?')[0].toUpperCase()
                        "
                        variant="light-primary"
                        class="mr-1"
                      />
                      <div style="flex: 1; min-width: 0">
                        <div
                          class="font-weight-bold text-truncate"
                          style="font-size: 13px"
                        >
                          {{ user.firstName }} {{ user.lastName }}
                        </div>
                        <div
                          class="text-muted text-truncate"
                          style="font-size: 11px"
                        >
                          {{ user.username }} &bull; {{ user.userId }}
                        </div>
                      </div>
                      <b-spinner
                        v-if="userPickerVerifyingId === user.userId"
                        small
                        class="ml-1"
                      />
                      <feather-icon
                        v-else-if="isUserConfirmed(user.userId)"
                        icon="CheckIcon"
                        size="16"
                        class="ml-1"
                        style="color: #28a745; flex-shrink: 0"
                      />
                    </div>
                    <div
                      v-if="userPickerLoading && userPickerUsers.length > 0"
                      class="text-center py-2 text-muted"
                    >
                      <b-spinner small /> Loading more...
                    </div>
                  </div>
                </b-card>
              </b-col>

              <!-- Confirmed Users List -->
              <b-col cols="12" md="6">
                <b-card
                  no-body
                  class="border"
                  style="overflow: hidden; min-height: 60px"
                >
                  <div style="max-height: 362px; overflow-y: auto">
                    <div
                      v-if="confirmedUsers.length === 0"
                      class="text-center text-muted py-3"
                      style="font-size: 13px"
                    >
                      No users selected yet.
                    </div>
                    <div
                      v-for="(u, idx) in confirmedUsers"
                      :key="u.userId"
                      class="d-flex align-items-center px-2 py-1"
                      style="
                        border-bottom: 1px solid #cce5ff;
                        background: #e8f4ff;
                      "
                    >
                      <b-avatar
                        size="28"
                        :text="
                          (u.firstName || u.username || '?')[0].toUpperCase()
                        "
                        variant="light-primary"
                        class="mr-1"
                      />
                      <div style="flex: 1; min-width: 0">
                        <div
                          class="font-weight-bold text-truncate"
                          style="font-size: 13px"
                        >
                          {{ u.firstName }} {{ u.lastName }}
                        </div>
                        <div
                          class="text-muted text-truncate"
                          style="font-size: 11px"
                        >
                          {{ u.username }} &bull; {{ u.userId }}
                        </div>
                      </div>
                      <b-button
                        size="sm"
                        variant="flat-danger"
                        class="p-0 ml-1"
                        style="line-height: 1; min-width: 24px"
                        @click="removeConfirmedUser(idx)"
                      >
                        <feather-icon icon="XIcon" size="14" />
                      </b-button>
                    </div>
                  </div>
                </b-card>
                <!-- Add to recurrence button -->
                <div class="mt-1 mb-1 text-right">
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    :disabled="confirmedUsers.length === 0"
                    @click="handleAddConfirmedUsersToItems"
                  >
                    <feather-icon
                      icon="ArrowRightIcon"
                      size="14"
                      class="mr-25"
                    />
                    Set Recurrence List Selected
                  </b-button>
                </div>
              </b-col>
            </b-row>

            <b-row>
              <!-- <b-col cols="12">
                <span class="text-muted text-sm"
                  >Drag items to the table on the right to set recurring
                  issuance</span
                ></b-col
              > -->
              <b-col>
                <draggable
                  :value="items"
                  @change="handleDrag"
                  @add="handleDrop"
                  group="tag"
                  class="w-100"
                >
                  <b-table
                    :items="items"
                    :fields="tableColumns"
                    responsive
                    thead-class="text-nowrap"
                    show-empty
                  >
                    <template #empty>
                      <div
                        v-if="items.length === 0"
                        class="text-center text-muted"
                      >
                        Drag profiles or segments here to set recurring issuance
                      </div>
                    </template>

                    <template #cell(profileSegment)="{ item }">
                      <b-media vertical-align="center">
                        <div class="d-flex align-items-center text-nowrap">
                          <span style="font-weight: 800">{{
                            item.profileSegment
                          }}</span>
                        </div>
                        <span style="font-size: 10px" v-if="item.selectedUsers"
                          >{{ item.selectedUsers.length }} customers from
                          {{ item.source ? "customers list" : "CSV" }}
                        </span>
                      </b-media>
                    </template>

                    <template #cell(recurrence)="{ item }">
                      <b-media vertical-align="center">
                        <div class="d-flex align-items-center text-nowrap">
                          {{ item.recurrence }}
                        </div>
                      </b-media>
                    </template>

                    <template #cell(actions)="{ item }">
                      <b-media vertical-align="center">
                        <div class="d-flex align-items-center">
                          <b-button
                            size="sm"
                            variant="outline-primary"
                            class="mr-1"
                            @click="selectedTagItem(item)"
                          >
                            Set Recurrence
                          </b-button>
                          <b-button
                            size="sm"
                            variant="outline-danger"
                            @click="deleteTagItem(item)"
                          >
                            Remove
                          </b-button>
                        </div>
                      </b-media>
                    </template>
                  </b-table>
                </draggable>
              </b-col>
            </b-row>
          </b-card>

          <b-row>
            <b-col cols="12">
              <b-card>
                <h5 class="font-weight-bold mb-0">Marketing Assets</h5>
                <div class="mt-1">
                  <b-tabs pills>
                    <b-tab active>
                      <template #title>
                        <span
                          class="d-flex align-items-center"
                          style="gap: 6px"
                        >
                          Banner
                          <feather-icon
                            :icon="
                              isBannerComplete
                                ? 'CheckCircleIcon'
                                : 'XCircleIcon'
                            "
                            size="16"
                            :class="
                              isBannerComplete ? 'text-success' : 'text-danger'
                            "
                          />
                        </span>
                      </template>
                      <b-row>
                        <b-col
                          md="3"
                          sm="6"
                          v-for="banner in banners"
                          :key="banner.key"
                        >
                          <b-form-group>
                            <p class="font-weight-bold mb-0">
                              {{ banner.label }}
                            </p>

                            <!-- File input -->
                            <b-form-file
                              v-model="banner.file"
                              accept="image/*"
                              @input="onFileChange(banner)"
                              placeholder="Choose File"
                              browse-text="Choose File"
                            ></b-form-file>

                            <small class="text-muted d-block mt-1">{{
                              banner.desc
                            }}</small>

                            <!-- Preview + Loading -->
                            <b-skeleton
                              v-if="banner.loading"
                              class="skeleton-box mx-auto rounded border"
                              style="width: 100%; min-height: 150px"
                            ></b-skeleton>

                            <img
                              v-else-if="
                                banner.previewUrl &&
                                formBanner.bannerPosition === banner.key
                              "
                              :src="banner.previewUrl"
                              alt="preview"
                              class="img-fluid rounded border"
                              style="
                                width: 100%;
                                min-width: 100%;
                                min-height: 150px;
                                height: 150px;
                                object-fit: cover;
                              "
                            />
                          </b-form-group>
                        </b-col>
                      </b-row>
                      <b-row>
                        <b-col>
                          <validation-provider
                            #default="{ errors }"
                            name="banner link"
                            :rules="
                              formBanner.bannerPosition ? 'required' : undefined
                            "
                          >
                            <b-form-group
                              label="Banner Link URL"
                              label-class="font-weight-bold"
                            >
                              <b-form-input
                                v-model="formBanner.bannerLink"
                                placeholder="https://example.com/promotion"
                              ></b-form-input>
                              <div>
                                <small class="text-danger">{{
                                  errors[0]
                                }}</small>
                              </div>
                              <small class="text-muted"
                                >Where should users go when they click the
                                banner?</small
                              >
                            </b-form-group>
                          </validation-provider>
                        </b-col>
                      </b-row>
                      <b-row>
                        <b-col cols="12">
                          <b-row>
                            <b-col cols="3">
                              <validation-provider
                                #default="{ errors }"
                                name="start date"
                                :rules="
                                  formBanner.bannerPosition
                                    ? 'required'
                                    : undefined
                                "
                              >
                                <b-form-group label="Start Date">
                                  <flat-pickr
                                    :value="formBanner.startDate"
                                    class="form-control"
                                    :config="configDateBanner"
                                    placeholder="dd/mm/yyyy"
                                    @input="
                                      (val) => (formBanner.startDate = val)
                                    "
                                  />
                                  <small class="text-danger">{{
                                    errors[0]
                                  }}</small>
                                </b-form-group>
                              </validation-provider>
                            </b-col>
                            <b-col cols="3">
                              <validation-provider
                                #default="{ errors }"
                                name="end date"
                                :rules="
                                  formBanner.bannerPosition
                                    ? 'required'
                                    : undefined
                                "
                              >
                                <b-form-group label="End Date">
                                  <flat-pickr
                                    :value="formBanner.endDate"
                                    class="form-control"
                                    :config="configDateBanner"
                                    placeholder="dd/mm/yyyy"
                                    @input="(val) => (formBanner.endDate = val)"
                                  />
                                  <small class="text-danger">{{
                                    errors[0]
                                  }}</small>
                                </b-form-group>
                              </validation-provider>
                            </b-col>
                          </b-row>
                        </b-col>
                      </b-row>

                      <b-row class="mt-1"
                        ><b-col>
                          <div
                            class="d-flex align-items-center justify-content-between"
                          >
                            <h6 class="font-weight-bold mb-0">
                              Desktop Preview
                            </h6>
                          </div>
                          <div class="mt-2">
                            <DesktopUI
                              :banners="banners"
                              :bannerPosition="formBanner.bannerPosition"
                            />
                          </div> </b-col
                      ></b-row>

                      <b-row class="mt-1">
                        <b-col cols="12">
                          <div
                            class="d-flex align-items-center justify-content-between"
                          >
                            <h6 class="font-weight-bold mb-0">
                              Mobile Preview
                            </h6>
                          </div>
                          <div class="mt-1">
                            <MobileUI :bannerCarousel="banners[0]" />
                          </div>
                        </b-col>
                      </b-row>
                    </b-tab>

                    <!-- INAPP TAB -->
                    <b-tab title="INAPP">
                      <p class="text-muted">INAPP configuration here</p>
                    </b-tab>

                    <!-- EMAIL TAB -->
                    <b-tab title="Email">
                      <p class="text-muted">Email template configuration</p>
                    </b-tab>

                    <!-- SMS TAB -->
                    <b-tab title="SMS">
                      <p class="text-muted">SMS content configuration</p>
                    </b-tab>
                  </b-tabs>
                </div>
              </b-card>
            </b-col>
            <b-col cols="12">
              <b-card>
                <h5 class="font-weight-bold mb-0">
                  Terms & Conditions (Mandatory)
                </h5>
                <div class="mt-1">
                  <b-form-group label="Choose Template">
                    <b-form-select
                      v-model="templateId"
                      :options="templates"
                    ></b-form-select>
                  </b-form-group>
                  <b-form-group label="Terms Content">
                    <b-form-textarea
                      v-model="termFinalContent"
                      rows="4"
                    ></b-form-textarea>
                    <small
                      >You can edit the template text before publishing</small
                    >
                  </b-form-group>
                </div>
              </b-card>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12" class="text-right">
              <b-button
                @click="$router.push('/promotions/odd-boosts')"
                variant="outline-secondary"
                class="mr-1"
              >
                Cancel
              </b-button>
              <b-button
                @click="
                  handleCreatePromotion({ status: 'inactive', action: 'draft' })
                "
                variant="outline-secondary"
                class="mr-1"
                :disabled="loadingAction === 'draft' && loadingCreatePromotion"
                style="min-width: 136px"
              >
                Save as Draft
              </b-button>
              <b-button
                :disabled="
                  loadingAction === 'publish' && loadingCreatePromotion
                "
                @click="
                  handleCreatePromotion({ status: 'active', action: 'publish' })
                "
                variant="primary"
                style="min-width: 172px"
                class="mr-1"
              >
                Save and Activate
              </b-button>

              <b-button
                :disabled="loadingAction === 'now' && loadingCreatePromotion"
                @click="
                  handleCreatePromotion({
                    status: 'active',
                    isNow: true,
                    action: 'now',
                  })
                "
                variant="primary"
                style="min-width: 172px"
              >
                Save and Activate Now
              </b-button>
            </b-col>
          </b-row>
        </b-col>
      </validation-observer>
    </b-row>

    <b-modal
      ref="tagRef"
      title="Set Recurring Issuance"
      centered
      cancel-disabled
      footer-class="border-0"
      size="md"
      no-close-on-backdrop
    >
      <b-container fluid>
        <b-row v-if="itemSelected">
          <b-col cols="12">
            <p class="text-body font-weight-bolder mb-0">Issuance Type</p>
            <v-select
              v-model="selectedIssuanceType"
              :options="issuanceType"
              :reduce="(val) => val"
              :clearable="false"
              class="w-100"
            />
          </b-col>
          <b-col
            v-if="selectedIssuanceType.value === '2'"
            cols="12"
            class="mt-1"
          >
            <p class="text-body font-weight-bolder mb-0">Every</p>
            <b-input-group prepend="Days">
              <b-form-input
                v-model="itemSelected.intervalDays"
                id="inline-form-input-username"
                type="number"
                value="1"
              ></b-form-input>
            </b-input-group>
          </b-col>
          <b-col
            v-if="selectedIssuanceType.value === '3'"
            cols="12"
            class="mt-1"
          >
            <p class="text-body font-weight-bolder mb-0">Select Days</p>
            <div class="d-flex flex-wrap" style="gap: 8px">
              <div v-for="option in dayOfWeek" :key="option.value">
                <b-form-checkbox
                  v-model="itemSelected.weekdays"
                  :value="option.value"
                  :name="option.text"
                >
                  {{ option.value }}
                </b-form-checkbox>
              </div>
            </div>
          </b-col>
          <b-col
            v-if="selectedIssuanceType.value === '4'"
            cols="12"
            class="mt-1"
          >
            <p class="text-body font-weight-bolder mb-0">Day of Month</p>
            <v-select
              v-model="itemSelected.dayOfMonth"
              :options="days"
              :reduce="(val) => val.value"
              :clearable="false"
              class="w-100"
            />
          </b-col>

          <template v-if="selectedIssuanceType.value === '5'">
            <b-col cols="12" class="mt-1">
              <p class="text-body font-weight-bolder mb-0">Occurrence</p>
              <v-select
                v-model="itemSelected.occurrence"
                :options="occurrences"
                :reduce="(val) => val.value"
                :clearable="false"
                class="w-100"
              />
            </b-col>
            <b-col cols="12" class="mt-1">
              <p class="text-body font-weight-bolder mb-0">Day of Week</p>
              <v-select
                id="dayOfWeek"
                v-model="itemSelected.weekday"
                :options="dayOfWeek"
                :reduce="(val) => val.value"
                class="w-100"
              />
            </b-col>
          </template>
          <b-col
            v-if="selectedIssuanceType.value === '6'"
            cols="12"
            class="mt-1"
          >
            <p class="text-body font-weight-bolder mb-0">Select Date</p>
            <flat-pickr
              id="date"
              v-model="date"
              class="form-control"
              :config="configDate"
              placeholder="mm/dd/yyyy"
            />
          </b-col>
          <b-col cols="12" class="mt-1">
            <p class="text-body font-weight-bolder mb-0">Number of Tokens</p>
            <b-form-input
              v-model="itemSelected.tokenPerIssuances"
              type="number"
              placeholder="Enter amount"
              value="1"
            ></b-form-input>
          </b-col>
        </b-row>
      </b-container>
      <template #modal-footer>
        <b-row class="w-100 mt-3">
          <b-button
            @click="refreshRecurring"
            variant="outline-secondary"
            class="ml-auto mr-1"
            >Cancel</b-button
          ><b-button
            :disabled="checkDisableSetRecurringIssuance()"
            variant="primary"
            @click="handleSaveIssuance"
            >Save</b-button
          >
        </b-row>
      </template>
    </b-modal>
  </div>
</template>

<script>
import Spinner from "@core/components/Spinner";
import {
  BButton,
  BCard,
  BCol,
  BPagination,
  BRow,
  BButtonGroup,
  BTable,
  BCardBody,
  BFormInput,
  BMedia,
  BAvatar,
  BProgress,
  BProgressBar,
  BFormTextarea,
  BFormCheckbox,
  BFormRadioGroup,
  BFormRadio,
  BFormGroup,
  BImg,
  BFormCheckboxGroup,
  BFormSelect,
  BInputGroup,
  BFormFile,
  BInputGroupAppend,
  BTabs,
  BTab,
  BContainer,
  BDropdown,
  BDropdownItem,
  BSkeleton,
  BSpinner,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import FeatherIcon from "@/@core/components/feather-icon/FeatherIcon.vue";
import DualListbox from "../../components/DuoListBox.vue";
import draggable from "vuedraggable";
import { v4 as uuidv4 } from "uuid";
import { mapState, mapActions } from "vuex";
import { formatDateSend, getOrdinalSuffix } from "@/helpers/utils";
import useToastHandler from "@/services/toastHandler";
import { parse } from "csv-parse";
import { Vue2TinymceEditor } from "vue2-tinymce-editor";
import MobileUI from "../../../marketing/BannerManagement/components/MobileUI.vue";
import DesktopUI from "../../../marketing/BannerManagement/components/DesktopUI.vue";
import { sanitizePayload } from "@/helpers/utils";

export default {
  components: {
    BButton,
    BCard,
    BRow,
    BCol,
    BTable,
    BMedia,
    BPagination,
    vSelect,
    Spinner,
    BButtonGroup,
    BTable,
    BCardBody,
    BFormInput,
    BAvatar,
    flatPickr,
    BProgress,
    BProgressBar,
    ValidationProvider,
    ValidationObserver,
    BFormTextarea,
    BFormCheckbox,
    BFormRadioGroup,
    BFormRadio,
    BFormGroup,
    BImg,
    BFormCheckboxGroup,
    BFormSelect,
    FeatherIcon,
    BInputGroup,
    BFormFile,
    BInputGroupAppend,
    DualListbox,
    BTabs,
    BTab,
    draggable,
    BContainer,
    BDropdown,
    BDropdownItem,
    Vue2TinymceEditor,
    MobileUI,
    DesktopUI,
    BSkeleton,
    BSpinner,
  },
  data() {
    const banners = [
      {
        key: "carousel",
        label: "Carousel Banner (Web & Mobile)",
        desc: "Inside website carousel - 1200x400px",
        width: 1200,
        height: 400,
        resourceId: null,
        previewUrl: null,
        loading: false,
      },
      {
        key: "top",
        label: "Top Banner",
        desc: "Full-width top banner - 1920x150px",
        width: 1920,
        height: 150,
        resourceId: null,
        previewUrl: null,
        loading: false,
      },
      {
        key: "left",
        label: "Left Sidebar Banner",
        desc: "Left side promotional banner - 220x600px",
        width: 220,
        height: 600,
        resourceId: null,
        previewUrl: null,
        loading: false,
      },
      {
        key: "right",
        label: "Right Sidebar Banner",
        desc: "Right side promotional banner - 300x600px",
        width: 300,
        height: 600,
        resourceId: null,
        previewUrl: null,
        loading: false,
      },
    ];
    const unitOptions = [
      {
        text: "%",
        value: "%",
      },
      {
        text: "Ladder Rolls",
        value: "ladder",
      },
      {
        text: "Amount",
        value: "amount",
      },
    ];
    return {
      max: 4,
      value: 2,
      name: "",
      description: "",
      startDate: new Date(),
      endDate: null,
      date: new Date(),
      desc: "",
      currentResource: null,
      configDate: {
        // dateFormat: 'd-m-Y',
        altFormat: "d-m-Y",
        altInput: true,
        static: true,
      },
      selected: "first",
      selectedBoostType: "Single Racing",
      optionsBoostType: [
        { label: "Racing Fixed Odds Boost", value: "Single Racing" },
        { label: "Racing SRM Boost", value: "SRM" },
        { label: "Racing Multi Boost", value: "Multi Racing" },
        { label: "Single Sport", value: "Single Sport" },
        { label: "Sport Multi Boost", value: "Multi Sport" },
        { label: "SGM Multi", value: "SGM" },
        // {
        //   label: "Exotic Boost (boosts the % rather than the odds)",
        //   value: "Exotic",
        // },
        { label: "Cross Multi Boost", value: "Cross Multi" },
        // { label: "Racing Futures", value: "" },
      ],
      options: [
        { text: "Static Image", value: "first" },
        { text: "Animated (GIF)", value: "second" },
        { text: "HTML5 Interactive", value: "third" },
      ],
      mainProps: {
        blank: true,
        blankColor: "#777",
        width: 32,
        height: 32,
      },
      selected: [],
      optionsCheck: [
        { text: "Homepage Hero", value: "1" },
        { text: "Sidebar", value: "2" },
        { text: "In-App Banner", value: "3" },
        { text: "Email Header", value: "4" },
      ],
      matchIndex: 0,
      matchPercentage: 25,
      marks: [25, 50, 75, 100, 125, 150, 175, 200],
      racingCodes: [
        {
          label: "Thoroughbreds",
          value: "R",
          type: "meetingType",
        },
        { label: "Greyhounds", value: "G", type: "meetingType" },
        { label: "Harness", value: "H", type: "meetingType" },
      ],
      betTypes: [
        { label: "Singles (Win)", value: "Win", type: "selectionType" },
        { label: "Singles (Place)", value: "Place", type: "selectionType" },
        { label: "BestDivSp", value: "best-div-sp", type: "selectionType" },
        { label: "MidDivWin", value: "mid-div-win", type: "selectionType" },
        { label: "MidDivPlace", value: "mid-div-place", type: "selectionType" },
        { label: "SP", value: "sp", type: "selectionType" },
        { label: "Multis", value: "multi", type: "selectionType" },
        { label: "Quinella", value: "quinella", type: "selectionType" },
        { label: "Exacta", value: "exacta", type: "selectionType" },
        { label: "Trifecta", value: "trifecta", type: "selectionType" },
        { label: "FirstFour", value: "first4", type: "selectionType" },
      ],
      regions: [
        { label: "Metro", value: "Metro", type: "venueType" },
        { label: "Country", value: "Country", type: "venueType" },
        { label: "Provincial", value: "Provincial", type: "venueType" },
      ],
      trackList: [],
      sports: [],
      leagues: [],
      eligibleMarkets: [
        { label: "Head to Head", value: "Head to Head", type: "marketName" },
        { label: "Line", value: "Line", type: "marketName" },
        { label: "Totals", value: "Totals", type: "marketName" },
        {
          label: "Winning Margin",
          value: "Winning Margin",
          type: "marketName",
        },
        {
          label: "Half-Time/Full-Time Double",
          value: "Half-Time/Full-Time Double",
          type: "marketName",
        },
        {
          label: "Both Teams to Score",
          value: "Both Teams to Score",
          type: "marketName",
        },
        {
          label: "Over/Under Goals",
          value: "Over/Under Goals",
          type: "marketName",
        },
        { label: "Correct Score", value: "Correct Score", type: "marketName" },
        {
          label: "First Try/Goalscorer",
          value: "First Try/Goalscorer",
          type: "marketName",
        },
        { label: "Win Draw Win", value: "Win Draw Win", type: "marketName" },
      ],
      boostAmount: null,
      boostUnit: unitOptions[0].value,
      unitOptions,
      maxStake: null,
      selectedMaxStakeType: "Amount",
      tagCustomerProfiles: [],
      tagCustomerSegments: [],
      tableColumns: [
        { key: "profileSegment", label: "Profile/Segment" },
        { key: "recurrence", label: "Recurrence" },
        { key: "actions", label: "Actions" },
      ],
      items: [],
      itemSelected: null,
      issuanceType: [
        { label: "Daily", value: "1", type: "every-x-days" },
        { label: "Every X Days", value: "2", type: "every-x-days" },
        { label: "Weekly (Selected Days)", value: "3", type: "weekly" },
        { label: "Monthly (Specific Date)", value: "4", type: "monthly" },

        {
          label: "Monthly (First/Last Day of Week)",
          value: "5",
          type: "monthly-weekday",
        },
        // {
        //   label: "Monthly (Calendar Selection)",
        //   value: "6",
        //   type: "monthly-weekday",
        // },
      ],
      selectedIssuanceType: {
        label: "Daily",
        value: "1",
        type: "every-x-days",
      },
      dayOfWeek: [
        { label: "Monday", value: "mon", id: 1 },
        { label: "Tuesday", value: "tue", id: 2 },
        { label: "Wed", value: "web", id: 3 },
        { label: "Thursday", value: "thu", id: 4 },
        { label: "Friday", value: "fri", id: 5 },
        { label: "Saturday", value: "sat", id: 6 },
        { label: "Sunday", value: "sun", id: 7 },
      ],
      occurrences: [
        { label: "First", value: "1", text: "first" },
        { label: "Second", value: "2", text: "second" },
        { label: "Third", value: "3", text: "third" },
        { label: "Fourth", value: "4", text: "fourth" },
        { label: "Last", value: "5", text: "last" },
      ],
      days: [],
      rules: [],
      selectedRules: {
        racingCodes: [],
        betTypes: [],
        regions: [],
        trackList: [],
        sports: [],
        eligibleMarkets: [],
      },
      activeStatus: "inactive",
      targets: [],

      additionalInfo: "",
      editorOptions: {
        forced_root_block: "div",
        height: 200,
        content_style: `
        html, body {
          background-color: #0766AA !important; 
          height: 100%;                         
          margin: 0;                        
          padding: 10px;               
          font-family: Arial, sans-serif;
          box-sizing: border-box;
        }
        
      `,
        forced_root_block_attrs: {
          style: "color: white;",
        },
      },
      templateId: null,
      termFinalContent: "",
      banners,
      formBanner: {
        bannerLink: "",
        bannerPosition: "",
        startDate: "",
        endDate: "",
      },
      configDateBanner: {
        enableTime: true,
        noCalendar: false,
        time_24hr: true,
        altFormat: "d-m-Y H:i",
      },
      templates: [],
      loadingAction: null,
      // User Picker
      userPickerSearch: "",
      userPickerUsers: [],
      userPickerLoading: false,
      userPickerPage: 1,
      userPickerHasMore: true,
      userPickerVerifyingId: null,
      confirmedUsers: [],
    };
  },
  computed: {
    ...mapState("promotions/list", ["loadingCreatePromotion"]),
    ...mapState("promotions/price-promotions", ["termConditions"]),
    isBannerComplete() {
      return !!(
        this.formBanner.bannerPosition &&
        this.formBanner.bannerLink &&
        this.formBanner.startDate &&
        this.formBanner.endDate
      );
    },
  },
  watch: {
    userPickerSearch: {
      handler() {
        clearTimeout(this._userPickerSearchTimer);
        this._userPickerSearchTimer = setTimeout(() => {
          this.handleUserPickerSearch();
        }, 300);
      },
    },
    selectedIssuanceType(val) {
      this.handleSelectedIssuanceType(val);
    },
    termConditions(val) {
      if (!val) return;
      this.templates = val.map((i) => {
        return {
          ...i,
          value: i.id,
          text: i.name,
        };
      });
      const isUpdate = this.$router.currentRoute.params.id;
      if (!isUpdate) {
        this.templateId = this.templates[0].value;
        this.termFinalContent = this.templates[0].text;
      }
    },
    templateId(val) {
      const selectedTemplate = this.termConditions.find((i) => i.id === val);
      this.termFinalContent = selectedTemplate ? selectedTemplate.content : "";
    },
  },
  async created() {
    await Promise.all([
      this.fetchSegmentList(),
      this.fetchProfileList(),
      this.fetchSports(),
      this.fetchMeetings(),
      this.getTermConditions(),
    ]);
    this.loadUserPickerPage();
    await this.fetchDetailPromotion();
    this.updatePercentage();
    for (let index = 1; index <= 31; index++) {
      this.days.push({
        label: getOrdinalSuffix(index),
        value: `${index}`,
      });
    }
    this.days.push({
      label: "Last",
      value: "last",
    });
  },
  methods: {
    ...mapActions({
      getSegmentsList: "segment/list/getSegmentsList",
      getProfileList: "user/list/getProfileList",
      createNewPromotion: "promotions/list/createNewPromotion",
      getDetailPromotion: "promotions/list/getDetailPromotion",
      updatePromotion: "promotions/list/updatePromotion",
      updatePromotionTargets: "promotions/list/updatePromotionTargets",
      getListSport: "sport/list/getListSport",
      getListMeetings: "trading/racing/getListMeetings",
      deletePromotionTarget: "promotions/list/deletePromotionTarget",
      createUserList: "user/list/createUserList",
      getUserList: "user/list/getUserListByGroup",
      getTermConditions: "promotions/price-promotions/getTermConditions",
      uploadBannerImage: "marketing/banner/uploadBannerImage",
      getAllUserList: "user/list/getUserList",
    }),
    async fetchSports() {
      const data = await this.getListSport({
        limit: 100,
        status: "active",
      });
      if (!data && data.data.length <= 0)
        return this.showToastError(false, "Error fetching sports list");
      this.sports = data.data.map((sport) => {
        return {
          label: sport.name,
          value: sport.id,
          type: "sportId",
        };
      });
    },
    async fetchMeetings() {
      const data = await this.getListMeetings({
        limit: 100,
        status: "active",
        source: "RAS",
      });
      if (!data && data.length <= 0)
        return this.showToastError(false, "Error fetching meetings list");
      this.trackList = data.map((meeting) => {
        return {
          label: meeting.venueName,
          value: meeting.venueName,
          type: "venueName",
        };
      });
    },

    updatePercentage() {
      this.matchPercentage = (+this.matchIndex + 1) * 25;
    },
    handleDrag(event) {},

    handleDrop(event) {
      const draggedTag = event.clone?.innerText?.trim();
      if (!draggedTag) return;
      const item = [
        ...this.tagCustomerProfiles,
        ...this.tagCustomerSegments,
      ].find((i) => i.profileSegment === draggedTag);
      this.items.splice(event.newIndex, 0, {
        profileSegment: draggedTag,
        recurrence: "No set",
        uuid: uuidv4(),
        ...item,
      });
    },
    deleteTagItem(item) {
      this.items = this.items.filter((i) => i.uuid !== item.uuid);
    },
    selectedTagItem(item) {
      this.itemSelected = item;
      this.$refs.tagRef.show();
    },
    handleSelectedIssuanceType(val) {
      this.itemSelected.type = val.type;
      if (val.value === "1") this.itemSelected.intervalDays = 1;
    },
    handleSelectMaxStake(type) {
      this.selectedMaxStakeType = type;
      if (type === "Profile Limits") {
        this.maxStake = "Profile Limits";
      } else {
        this.maxStake = null;
      }
    },
    async fetchSegmentList() {
      const data = await this.getSegmentsList({
        limit: 100,
      });
      this.tagCustomerSegments = data.data.map((i) => {
        return {
          profileSegment: i.name,
          id: i.id,
          targetType: "CustomerSegment",
          recurrence: "No set",
          type: "every-x-days",
          intervalDays: 1,
          weekdays: [],
          weekday: "",
          dayOfMonth: null,
          occurrence: null,
          tokenPerIssuances: null,
        };
      });
    },
    async fetchProfileList() {
      const data = await this.getProfileList();
      this.tagCustomerProfiles = data.map((i) => {
        return {
          profileSegment: i.profileTypeId,
          id: i.profileTypeId,
          targetType: "Profile",
          recurrence: "No set",
          type: "every-x-days",
          intervalDays: 1,
          weekdays: [],
          weekday: "",
          dayOfMonth: null,
          occurrence: null,
          tokenPerIssuances: null,
        };
      });
    },
    mapBannerForm(data) {
      if (!data) return;
      this.formBanner = {
        ...this.formBanner,
        bannerId: data.bannerId,
        startDate: data.startDate,
        endDate: data.endDate,
        bannerLink: data.bannerLink,
        bannerPosition: data.bannerPosition,
        platform: data.platform,
      };
    },
    mapBannerResource(data) {
      if (!data.resources?.length) return;
      const index = this.banners.findIndex(
        (b) => b.key === data.bannerPosition
      );
      if (index === -1) return;
      const resource = data.resources[0];
      this.banners[index] = {
        ...this.banners[index],
        resourceId: resource?.resourceId || null,
        previewUrl: resource?.url || null,
      };
    },
    async fetchDetailPromotion() {
      if (!this.$router.currentRoute.params.id) return;
      const { data: data } = await this.getDetailPromotion(
        this.$router.currentRoute.params.id
      );
      this.name = data.name;
      this.description = data.description;
      this.additionalInfo = data.additionalInfo;
      this.selectedBoostType = data.subType;
      this.boostUnit =
        data.discountType === "percent" ? "%" : data.discountType;
      this.status = data.status;
      this.maxStake = data.maxDiscount || null;
      this.startDate = new Date(data.startTime);
      this.endDate = new Date(data.endTime);
      this.boostAmount = data.discountValue;
      this.templateId = data.templateId || null;
      this.$nextTick(() => {
        this.termFinalContent = data.termFinalContent || "";
      });
      if (data.banner) {
        this.currentResource = data.banner?.resources?.[0] || null;
        this.mapBannerForm(data.banner);
        this.mapBannerResource(data.banner);
      }

      const list = data.rules
        .filter((i) => i.conditionType && i.conditionValue)
        .map((rule) => {
          return {
            appliableType: rule.conditionType,
            appliableId: rule.conditionValue,
          };
        });

      this.rules = list;

      this.selectedRules.racingCodes = list
        .filter((i) => i.appliableType === "meetingType")
        .map((i) => {
          const item = this.racingCodes.find((r) => r.value === i.appliableId);
          return item;
        });

      this.selectedRules.betTypes = list
        .filter((i) => i.appliableType === "selectionType")
        .map((i) => {
          const item = this.betTypes.find((r) => r.value === i.appliableId);
          return item;
        });

      this.selectedRules.regions = list
        .filter((i) => i.appliableType === "venueType")
        .map((i) => {
          const item = this.regions.find((r) => r.value === i.appliableId);
          return item;
        });

      this.selectedRules.sports = list
        .filter((i) => i.appliableType === "sportId")
        .map((i) => {
          const item = this.sports.find((r) => r.value === i.appliableId);
          return item;
        });

      this.selectedRules.eligibleMarkets = list
        .filter((i) => i.appliableType === "marketName")
        .map((i) => {
          const item = this.eligibleMarkets.find(
            (r) => r.value === i.appliableId
          );
          return item;
        });

      this.selectedRules.trackList = list
        .filter((i) => i.appliableType === "venueName")
        .map((i) => {
          const item = this.trackList.find((r) => r.value === i.appliableId);
          return item;
        });

      this.targets = data.targets;
      this.items = await Promise.all(
        data.targets.map(async (target) => {
          const item = [
            ...this.tagCustomerProfiles,
            ...this.tagCustomerSegments,
          ].find((i) => i.id === target.targetId);

          const weekdays =
            typeof target.recurring.weekdays === "string"
              ? JSON.parse(target.recurring.weekdays).map((day) => {
                  const d = this.dayOfWeek.find((i) => i.id === day);
                  return d.value;
                })
              : null;

          let list;

          if (target && target.targetType === "UserList") {
            list = await this.fetchUserList(target.targetId);
          }

          return {
            ...target.recurring,
            uuid: target.id,
            profileSegment: item
              ? item.profileSegment
              : target.targetType === "User"
              ? "User"
              : "Untitled",
            targetType: target.targetType,
            id: target.targetId,
            targetId: target.targetId,
            recurrence:
              target.recurring.tokenPerIssuances +
              " token(s) " +
              this.formatText({
                ...target.recurring,
                weekdays,
              }),
            weekdays,
            selectedUsers: list,
          };
        })
      );
    },
    async fetchUserList(id) {
      const res = await this.getUserList(id);
      return res.data.map((i) => i.userId);
    },

    // User Picker methods
    handleUserPickerSearch() {
      this.userPickerUsers = [];
      this.userPickerPage = 1;
      this.userPickerHasMore = true;
      this.loadUserPickerPage();
    },
    async loadUserPickerPage() {
      if (this.userPickerLoading || !this.userPickerHasMore) return;
      this.userPickerLoading = true;
      try {
        const limit = 20;
        const data = await this.getAllUserList({
          search: this.userPickerSearch || "",
          limit,
          page: this.userPickerPage,
        });
        if (!data || data.length === 0) {
          this.userPickerHasMore = false;
        } else {
          this.userPickerUsers = [...this.userPickerUsers, ...data];
          this.userPickerPage += 1;
          if (data.length < limit) this.userPickerHasMore = false;
        }
      } catch (e) {
        this.userPickerHasMore = false;
      } finally {
        this.userPickerLoading = false;
      }
    },
    handleUserListScroll(e) {
      const el = e.target;
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 60;
      if (nearBottom && this.userPickerHasMore && !this.userPickerLoading) {
        this.loadUserPickerPage();
      }
    },
    isUserConfirmed(userId) {
      return this.confirmedUsers.some((u) => u.userId === userId);
    },
    async handleSelectUser(user) {
      if (this.userPickerVerifyingId) return;
      // If already selected, untick / remove
      if (this.isUserConfirmed(user.userId)) {
        this.confirmedUsers = this.confirmedUsers.filter(
          (u) => u.userId !== user.userId
        );
        return;
      }
      this.userPickerVerifyingId = user.userId;
      try {
        const verified = true;
        // const verified = await this.fetchUser({ id: user.userId });
        if (verified) {
          this.confirmedUsers = [...this.confirmedUsers, user];
          this.showToastSuccess(
            "success",
            `User "${user.username || user.userId}" added.`
          );
        } else {
          this.showToastError(false, "User verification failed.");
        }
      } catch (e) {
        this.showToastError(false, "User verification failed.");
      } finally {
        this.userPickerVerifyingId = null;
      }
    },
    removeConfirmedUser(idx) {
      this.confirmedUsers.splice(idx, 1);
    },
    handleAddConfirmedUsersToItems() {
      if (!this.confirmedUsers.length) return;
      const newItem = {
        profileSegment: "Untitled",
        recurrence: "No set",
        uuid: uuidv4(),
        selectedUsers: this.confirmedUsers.map((u) => u.userId),
        type: "every-x-days",
        intervalDays: 1,
        weekdays: [],
        weekday: "",
        dayOfMonth: null,
        occurrence: null,
        tokenPerIssuances: null,
        targetType: "UserList",
        source: "customer",
      };
      this.items = [...this.items, newItem];
      this.confirmedUsers = [];
      this.selectedTagItem(newItem);
    },
    refreshRecurring() {
      this.selectedIssuanceType = {
        label: "Daily",
        value: "1",
        type: "every-x-days",
      };
      this.itemSelected = {
        profileSegment: "",
        recurrence: "No set",
        type: "every-x-days",
        intervalDays: 1,
        weekdays: [],
        weekday: "",
        dayOfMonth: null,
        occurrence: null,
        tokenPerIssuances: null,
      };
      this.$refs.tagRef.hide();
    },
    checkDisableSetRecurringIssuance() {
      return (
        !this.itemSelected.tokenPerIssuances ||
        (this.itemSelected.type === "every-x-days" &&
          !this.itemSelected.intervalDays) ||
        (this.itemSelected.type === "weekly" &&
          (!this.itemSelected.weekdays ||
            !this.itemSelected.weekdays.length)) ||
        (this.itemSelected.type === "monthly" && !this.itemSelected.dayOfMonth)
      );
    },
    formatText(item) {
      switch (item.type) {
        case "every-x-days":
          return item.intervalDays === 1
            ? "daily"
            : `every ${item.intervalDays} days`;
        case "weekly":
          return `weekly (${item.weekdays.join(", ")})`;
        case "monthly":
          return "monthly on the " + getOrdinalSuffix(Number(item.dayOfMonth));
        case "monthly-weekday":
          const occurrence = this.occurrences.find(
            (i) => i.value === item.occurrence
          );
          const weekday = this.dayOfWeek.find((i) => i.value === item.weekday);
          return `monthly on the ${occurrence.text} ${weekday.label}`;
        default:
          return "daily";
      }
    },
    handleSaveIssuance() {
      if (!this.itemSelected.type) {
        this.itemSelected.type === "every-x-days";
        this.itemSelected.intervalDays = 1;
      }

      this.items = this.items.map((i) => {
        if (i.uuid === this.itemSelected.uuid)
          return {
            ...this.itemSelected,
            recurrence:
              this.itemSelected.tokenPerIssuances +
              " token(s) " +
              this.formatText(this.itemSelected),
          };
        return i;
      });
      this.refreshRecurring();
    },
    async handleCreatePromotion({ status, isNow = false, action }) {
      const valid = await this.$refs.validateOddBoostForm.validate();
      if (!valid) return;
      const isUpdate = !!this.$router.currentRoute.params.id;
      this.activeStatus = status;
      this.loadingAction = action;
      const { currentResource } = this;
      const formatRules = this.rules.map((i) => {
        return {
          conditionType: i.appliableType,
          conditionValue: i.appliableId,
        };
      });

      const formatListTargets = await Promise.all(
        this.items.map(async (i) => {
          const listWeekdays = !!i.weekday ? [i.weekday] : i.weekdays;

          let targetUserListId;
          if (i && i.targetType === "UserList" && !i.targetId) {
            const resListId = await this.createUserList({
              userIds: i.selectedUsers,
            });
            if (resListId.data) targetUserListId = resListId.data;
          }

          return {
            targetType: i.targetType,
            targetId:
              i.targetType === "UserList" || i.targetType === "User"
                ? i.targetId
                  ? i.targetId
                  : targetUserListId
                : i.targetType === "CustomerSegment"
                ? i.id
                : i.profileSegment,
            recurring: {
              type: i.type,
              ...(!!i.intervalDays &&
                i.type === "every-x-days" && {
                  intervalDays: Number(i.intervalDays),
                }),
              ...(!!i.dayOfMonth && { dayOfMonth: Number(i.dayOfMonth) }),
              ...(!!i.occurrence && { occurrence: Number(i.occurrence) }),
              tokenPerIssuances: Number(i.tokenPerIssuances),
              startDate: this.formatDateSend(
                isNow ? new Date() : this.startDate
              ),
              endDate: this.formatDateSend(this.endDate),
              ...(Array.isArray(listWeekdays) &&
                listWeekdays.length > 0 && {
                  weekdays: listWeekdays.map((day) => {
                    const dayItem = this.dayOfWeek.find((d) => d.value === day);
                    return dayItem.id;
                  }),
                }),
            },
          };
        })
      );
      const { startDate, endDate, ...restFormBanner } = this.formBanner;
      const bannerInfo = {
        ...restFormBanner,
      };
      const newId = this.banners.find(
        (b) => b.key === bannerInfo.bannerPosition
      )?.resourceId;
      if (!isUpdate) {
        bannerInfo.resourceIds = this.banners
          .filter((b) => b.resourceId)
          .map((b) => b.resourceId);
      } else if (newId) {
        const oldId = currentResource?.resourceId;
        if (!oldId) {
          bannerInfo.resourceIds = [newId];
        } else if (oldId !== newId) {
          bannerInfo.resources = [{ oldResourceId: oldId, resourceId: newId }];
        }
      }
      if (startDate && endDate) {
        bannerInfo["startDate"] = this.formatDateSend(startDate);
        bannerInfo["endDate"] = this.formatDateSend(endDate);
        bannerInfo["platform"] = "all";
      }
      const data = sanitizePayload({
        name: this.name,
        description: this.description,
        additionalInfo: this.additionalInfo,
        type: "Boost",
        subType: this.selectedBoostType,
        discountType: this.boostUnit === "%" ? "percent" : this.boostUnit,
        discountValue: Number(this.boostAmount),
        ...(this.selectedMaxStakeType === "Amount" && {
          maxDiscount: Number(this.maxStake),
        }),
        status,
        startTime: this.formatDateSend(this.startDate),
        endTime: this.formatDateSend(this.endDate),
        rules: formatRules,
        bannerInfo,
        templateId: this.templateId,
        termFinalContent: this.termFinalContent,
      });
      if (isUpdate) {
        await Promise.all([
          this.handleUpdatePromotion(data),
          this.handleUpdatePromotionTargets(formatListTargets),
        ]);
        this.showToastSuccess(
          "success",
          "This Odds Boost updated successfully!"
        );
        return;
      }
      const res = await this.createNewPromotion({
        ...data,
        targets: formatListTargets,
      });
      if (res.data) {
        this.showToastSuccess(
          "success",
          "New Odds Boost created successfully!"
        );
        this.$router.push("/promotions/odd-boost");
      }
    },
    async handleUpdatePromotion(data) {
      const res = await this.updatePromotion({
        promotionId: this.$router.currentRoute.params.id,
        ...data,
      });
      return res;
    },
    convertListToIdsObject(l) {
      return l.reduce((acc, item, index) => {
        const key = `ids[${index}]`;

        acc[key] = item.id;

        return acc;
      }, {});
    },
    async handleUpdatePromotionTargets(list) {
      const resDelete = await this.deletePromotionTarget(
        this.convertListToIdsObject(this.targets)
      );
      if (!resDelete) return;
      const res = await this.updatePromotionTargets({
        promotionId: this.$router.currentRoute.params.id,
        targets: list,
      });
      return res;
    },
    handleSelectAppliesRacingCodes(list) {
      const applies = list.map((item) => ({
        appliableType: item.type,
        appliableId: item.value,
      }));
      const listNoThisType = this.rules.filter(
        (i) => i.appliableType !== "meetingType"
      );
      this.rules = [...listNoThisType, ...applies];
    },
    handleSelectAppliesBetTypes(list) {
      const applies = list.map((item) => ({
        appliableType: item.type,
        appliableId: item.value,
      }));
      const listNoThisType = this.rules.filter(
        (i) => i.appliableType !== "selectionType"
      );
      this.rules = [...listNoThisType, ...applies];
    },
    handleSelectAppliesRegions(list) {
      const applies = list.map((item) => ({
        appliableType: item.type,
        appliableId: item.value,
      }));
      const listNoThisType = this.rules.filter(
        (i) => i.appliableType !== "venueType"
      );
      this.rules = [...listNoThisType, ...applies];
    },
    handleSelectAppliesTrackList(list) {
      const applies = list.map((item) => ({
        appliableType: item.type,
        appliableId: item.value,
      }));
      const listNoThisType = this.rules.filter(
        (i) => i.appliableType !== "venueName"
      );
      this.rules = [...listNoThisType, ...applies];
    },
    handleSelectAppliesSports(list) {
      const applies = list.map((item) => ({
        appliableType: item.type,
        appliableId: item.value,
      }));
      const listNoThisType = this.rules.filter(
        (i) => i.appliableType !== "sportId"
      );
      this.rules = [...listNoThisType, ...applies];
    },
    handleSelectAppliesEligibleMarkets(list) {
      const applies = list.map((item) => ({
        appliableType: item.type,
        appliableId: item.value,
      }));
      const listNoThisType = this.rules.filter(
        (i) => i.appliableType !== "marketName"
      );
      this.rules = [...listNoThisType, ...applies];
    },
    onClickInput() {
      const fileInput = this.$refs.fileInput;
      fileInput.click();
    },
    _resetInputFile() {
      this.$refs.fileInput.value = null;
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      const reader = new FileReader();
      reader.onload = () => {
        this.parseCSV(reader.result);
      };
      reader.readAsText(file);
      this._resetInputFile();
    },
    parseCSV(csvData) {
      parse(csvData, { delimiter: "," }, (err, data) => {
        if (err) {
          console.error(err);
          return;
        }
        data.shift();
        const newList = data.map((i) => i[0]);

        if (newList.length > 0) {
          this.items = [
            ...this.items,
            {
              profileSegment: "Untitled",
              recurrence: "No set",
              uuid: uuidv4(),
              targetType: "UserList",
              recurrence: "No set",
              type: "every-x-days",
              intervalDays: 1,
              weekdays: [],
              weekday: "",
              dayOfMonth: null,
              occurrence: null,
              tokenPerIssuances: null,
              selectedUsers: newList,
            },
          ];
        }
      });
    },
    async onFileChange(banner) {
      if (!banner.file) return;
      try {
        this.formBanner.bannerPosition = banner.key;
        banner.loading = true;
        const result = await this.uploadBannerImage(banner);
        banner.previewUrl = result[0].url;
        banner.resourceId = result[0].resourceId;
        banner.loading = false;
      } catch (error) {
        banner.loading = false;
      }
    },
  },
  setup() {
    const { showToastSuccess, showToastError } = useToastHandler();

    return {
      showToastSuccess,
      showToastError,

      formatDateSend,
    };
  },
};
</script>
<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";
@import "@core/scss/vue/libs/vue-select.scss";
</style>
